import Hls from "hls.js";
import { WatchClock } from "@/services/watchClock";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export type VideoPlayerHandle = {
  seekTo: (time: number) => void;
  restart: () => void;
  play: () => void;
};

type Props = {
  src: string;
  poster: string;
  title: string;
  trimStart: number;
  trimEnd: number;
  initialTime?: number;
  onProgress?: (time: number) => void;
  onPlay?: () => void;
  onPlaying?: () => void;
  onWaiting?: () => void;
  onEnded?: () => void;
  onPause?: () => void;
  onRetry?: () => void;
  onWatch?: (seconds: number) => void;
  onDuration?: (seconds: number) => void;
  onError?: (detail: PlaybackErrorDetail) => void;
};

export type PlaybackErrorDetail = {
  source: "hls" | "native" | "unsupported";
  type: string;
  detail: string;
  fatal: boolean;
  httpStatus?: number;
  host?: string;
  position: number;
  readyState: number;
  networkState: number;
};

const sourceHost = (value?: string) => {
  try { return value ? new URL(value, location.href).host.slice(0, 200) : undefined; }
  catch { return undefined; }
};
const CONTROLS_VISIBLE_MS = 12000;

const VideoPlayer = forwardRef<VideoPlayerHandle, Props>(
  (
    {
      src,
      poster,
      title,
      trimStart,
      initialTime = 0,
      onProgress,
      onPlay,
      onPlaying,
      onWaiting,
      onEnded,
      onPause,
      onRetry,
      onWatch,
      onDuration,
      onError,
    },
    ref,
  ) => {
    const videoRef = useRef<HTMLVideoElement>(null),
      progressRef = useRef(onProgress),
      playRef = useRef(onPlay),
      playingRef=useRef(onPlaying),waitingRef=useRef(onWaiting),endedRef=useRef(onEnded),errorRef=useRef(onError),retryRef=useRef(onRetry),
      completedRef=useRef(false),
      hlsRef = useRef<Hls | null>(null),
      playerRef = useRef<HTMLDivElement>(null),
      scrubActiveRef = useRef(false),
      lastScrubSeekRef = useRef(0),
      controlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null),
      gestureTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null),
      singleTapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null),
      gestureStartRef = useRef({ x: 0, y: 0, zone: "center" as "left" | "center" | "right", long: false, startTime: 0, targetTime: 0, wasPlaying: false, controlsWereVisible: true }),
      lastTapRef = useRef({ at: 0, zone: "center" as "left" | "center" | "right" }),
      [error, setError] = useState("");
    const [attempt, setAttempt] = useState(0), [slow, setSlow] = useState(false);
    const [paused, setPaused] = useState(true), [currentTime, setCurrentTime] = useState(0), [duration, setDuration] = useState(0);
    const [controlsVisible, setControlsVisible] = useState(true), [fullscreen, setFullscreen] = useState(false), [immersive, setImmersive] = useState(false);
    const [volume, setVolume] = useState(1), [muted, setMuted] = useState(false), [playbackRate, setPlaybackRate] = useState(1);
    const [quality, setQuality] = useState(-1), [qualities, setQualities] = useState<Array<{ index: number; label: string }>>([]);
    const [pictureInPicture, setPictureInPicture] = useState(false);
    const [gestureHint, setGestureHint] = useState<{ zone: "left" | "center" | "right"; text: string } | null>(null);
    const [scrubPreview, setScrubPreview] = useState<{ time: number } | null>(null);
    const retryPosition = useRef<number | null>(null);
    const autoRecovery = useRef(0);
    const watchRef = useRef(onWatch), pauseRef = useRef(onPause);
    const durationRef = useRef(onDuration);
    durationRef.current = onDuration;
    watchRef.current = onWatch; pauseRef.current = onPause;
    progressRef.current = onProgress;
    playRef.current = onPlay;
    playingRef.current=onPlaying;waitingRef.current=onWaiting;endedRef.current=onEnded;errorRef.current=onError;retryRef.current=onRetry;

    const formatTime = (seconds: number) => {
      if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
      const whole = Math.floor(seconds), hours = Math.floor(whole / 3600), minutes = Math.floor((whole % 3600) / 60), rest = whole % 60;
      return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}` : `${minutes}:${String(rest).padStart(2, "0")}`;
    };
    const scrubPreviewStyle = (time: number) => {
      const percent = Math.max(0, Math.min(100, duration > 0 ? (time / duration) * 100 : 0));
      return { left: `${percent}%`, transform: percent < 16 ? "translateX(0)" : percent > 84 ? "translateX(-100%)" : "translateX(-50%)" };
    };
    const scheduleControls = () => {
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
      setControlsVisible(true);
      if (!videoRef.current?.paused) controlsTimerRef.current = setTimeout(() => { setControlsVisible(false); setScrubPreview(null); scrubActiveRef.current = false; }, CONTROLS_VISIBLE_MS);
    };
    const togglePlayback = () => {
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) void video.play().catch(() => {});
      else video.pause();
      scheduleControls();
    };
    const seek = (value: number) => {
      const video = videoRef.current;
      if (!video || !Number.isFinite(value)) return;
      video.currentTime = value;
      setCurrentTime(value);
      scheduleControls();
    };
    const skip = (seconds: number) => seek(Math.max(0, Math.min(duration || Number.MAX_SAFE_INTEGER, (videoRef.current?.currentTime || 0) + seconds)));
    const clearGestureTimers = () => {
      if (gestureTimerRef.current) clearTimeout(gestureTimerRef.current);
      gestureTimerRef.current = null;
    };
    const flashGesture = (zone: "left" | "center" | "right", text: string, milliseconds = 650) => {
      setGestureHint({ zone, text });
      window.setTimeout(() => setGestureHint(current => current?.text === text ? null : current), milliseconds);
    };
    const gestureZone = (clientX: number) => {
      const box = playerRef.current?.getBoundingClientRect();
      if (!box) return "center" as const;
      const ratio = (clientX - box.left) / box.width;
      return ratio < 0.34 ? "left" as const : ratio > 0.66 ? "right" as const : "center" as const;
    };
    const beginGesture = (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      const zone = gestureZone(event.clientX);
      const video = videoRef.current;
      const startTime = Math.max(0, video?.currentTime || 0);
      gestureStartRef.current = { x: event.clientX, y: event.clientY, zone, long: false, startTime, targetTime: startTime, wasPlaying: Boolean(video && !video.paused), controlsWereVisible: controlsVisible };
      clearGestureTimers();
      gestureTimerRef.current = setTimeout(() => {
        gestureStartRef.current.long = true;
        scrubActiveRef.current = true;
        video?.pause();
        setControlsVisible(true);
        setGestureHint(null);
        setScrubPreview({ time: startTime });
        if (video && Number.isFinite(video.duration)) video.currentTime = Math.min(video.duration - 0.01, startTime + 0.01);
      }, 400);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    };
    const moveGesture = (event: React.PointerEvent<HTMLDivElement>) => {
      const start = gestureStartRef.current;
      const distance = Math.hypot(event.clientX - start.x, event.clientY - start.y);
      if (!start.long && distance > 12) {
        clearGestureTimers();
        return;
      }
      if (!start.long) return;
      event.preventDefault();
      const width = Math.max(1, playerRef.current?.clientWidth || 1);
      const ratio = Math.max(-1, Math.min(1, (event.clientX - start.x) / width));
      const acceleratedRatio = Math.sign(ratio) * Math.pow(Math.abs(ratio), 1.6);
      const target = Math.max(0, Math.min(duration || Number.MAX_SAFE_INTEGER, start.startTime + acceleratedRatio * (duration || 150)));
      start.targetTime = target;
      setCurrentTime(target);
      setScrubPreview({ time: target });
      const now = performance.now();
      if (videoRef.current && now - lastScrubSeekRef.current >= 100) {
        lastScrubSeekRef.current = now;
        videoRef.current.currentTime = target;
      }
    };
    const endGesture = (event: React.PointerEvent<HTMLDivElement>) => {
      const start = gestureStartRef.current;
      const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y) > 12;
      clearGestureTimers();
      if (start.long) {
        scrubActiveRef.current = false;
        seek(start.targetTime);
        setGestureHint(null); setScrubPreview(null);
        void videoRef.current?.play().catch(() => {});
        scheduleControls();
        return;
      }
      if (moved) return;
      const now = Date.now(), doubleTap = lastTapRef.current.zone === start.zone && now - lastTapRef.current.at < 300;
      if (doubleTap) {
        if (singleTapTimerRef.current) clearTimeout(singleTapTimerRef.current);
        singleTapTimerRef.current = null;
        lastTapRef.current.at = 0;
        if (start.zone === "left") { skip(-10); flashGesture("left", "后退 10 秒"); }
        else if (start.zone === "right") { skip(10); flashGesture("right", "快进 10 秒"); }
        else { togglePlayback(); flashGesture("center", videoRef.current?.paused ? "已暂停" : "播放"); }
        return;
      }
      lastTapRef.current = { at: now, zone: start.zone };
      if (singleTapTimerRef.current) clearTimeout(singleTapTimerRef.current);
      singleTapTimerRef.current = setTimeout(() => {
        if (paused || !start.controlsWereVisible) scheduleControls();
        else {
          if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
          controlsTimerRef.current = null;
          setControlsVisible(false);
          setScrubPreview(null);
          scrubActiveRef.current = false;
        }
        singleTapTimerRef.current = null;
      }, 300);
    };
    const cancelGesture = () => {
      const start = gestureStartRef.current;
      clearGestureTimers(); setGestureHint(null); setScrubPreview(null);
      if (start.long) {
        scrubActiveRef.current = false;
        if (videoRef.current) videoRef.current.currentTime = start.startTime;
        setCurrentTime(start.startTime);
        if (start.wasPlaying) void videoRef.current?.play().catch(() => {});
      }
    };
    const beginProgressScrub = () => {
      const video = videoRef.current;
      if (!video) return;
      scrubActiveRef.current = true;
      video.pause();
      setControlsVisible(true);
      setScrubPreview({ time: video.currentTime });
      if (Number.isFinite(video.duration)) video.currentTime = Math.min(video.duration - 0.01, video.currentTime + 0.01);
    };
    const updateProgressScrub = (target: number) => {
      const video = videoRef.current;
      if (!video || !Number.isFinite(target)) return;
      const next = Math.max(0, Math.min(duration || Number.MAX_SAFE_INTEGER, target));
      scrubActiveRef.current = true;
      setCurrentTime(next);
      setScrubPreview({ time: next });
      video.currentTime = next;
    };
    const finishProgressScrub = () => {
      const video = videoRef.current;
      scrubActiveRef.current = false;
      setScrubPreview(null);
      if (video) void video.play().catch(() => {});
      scheduleControls();
    };
    const changeVolume = (value: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.volume = value;
      video.muted = value === 0;
      setVolume(value);
      setMuted(value === 0);
      scheduleControls();
    };
    const toggleMute = () => {
      const video = videoRef.current;
      if (!video) return;
      video.muted = !video.muted;
      setMuted(video.muted);
      scheduleControls();
    };
    const changePlaybackRate = (value: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.playbackRate = value;
      setPlaybackRate(value);
      scheduleControls();
    };
    const changeQuality = (value: number) => {
      if (hlsRef.current) hlsRef.current.currentLevel = value;
      setQuality(value);
      scheduleControls();
    };
    const togglePictureInPicture = async () => {
      const video = videoRef.current;
      if (!video || !("pictureInPictureEnabled" in document)) return;
      try {
        if (document.pictureInPictureElement) await document.exitPictureInPicture();
        else await video.requestPictureInPicture();
      } catch { /* Picture-in-picture may be blocked by the browser or WebView. */ }
      scheduleControls();
    };
    const toggleFullscreen = async () => {
      const player = playerRef.current;
      if (!player) return;
      const orientation = screen.orientation as ScreenOrientation & { lock?: (value: "landscape") => Promise<void>; unlock?: () => void };
      const documentWithWebkit = document as Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => Promise<void> | void };
      const playerWithWebkit = player as HTMLDivElement & { webkitRequestFullscreen?: () => Promise<void> | void };
      try {
        if (document.fullscreenElement || documentWithWebkit.webkitFullscreenElement) {
          if (document.exitFullscreen) await document.exitFullscreen();
          else await documentWithWebkit.webkitExitFullscreen?.();
          setImmersive(false);
          return;
        }
        if (player.requestFullscreen) await player.requestFullscreen();
        else if (playerWithWebkit.webkitRequestFullscreen) await playerWithWebkit.webkitRequestFullscreen();
        else setImmersive(value => !value);
        try { await orientation.lock?.("landscape"); } catch { /* Orientation lock is not available in every browser. */ }
      } catch {
        setImmersive(value => !value);
      }
      scheduleControls();
    };

    useEffect(() => {
      const documentWithWebkit = document as Document & { webkitFullscreenElement?: Element };
      const changed = () => {
        const orientation = screen.orientation as ScreenOrientation & { unlock?: () => void };
        const active = Boolean(document.fullscreenElement || documentWithWebkit.webkitFullscreenElement);
        setFullscreen(active);
        if (!active) { setImmersive(false); try { orientation.unlock?.(); } catch { /* Unsupported browser. */ } }
        setControlsVisible(true);
      };
      document.addEventListener("fullscreenchange", changed);
      document.addEventListener("webkitfullscreenchange", changed);
      return () => {
        document.removeEventListener("fullscreenchange", changed);
        document.removeEventListener("webkitfullscreenchange", changed);
        if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
        clearGestureTimers();
        if (singleTapTimerRef.current) clearTimeout(singleTapTimerRef.current);
      };
    }, []);

    useEffect(() => {
      if (!immersive) return;
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      setFullscreen(true);
      return () => { document.body.style.overflow = previous; setFullscreen(false); };
    }, [immersive]);

    useImperativeHandle(ref, () => ({
      seekTo(time) {
        const video = videoRef.current;
        if (video) {
          // Preview timestamps are relative to the generated preview range,
          // which still starts after the opening offset.
          video.currentTime = time + trimStart;
          void video.play().catch(() => {});
        }
      },
      restart() {
        const video = videoRef.current;
        if (video) { video.currentTime = 0; void video.play().catch(() => {}); }
      },
      play() {
        void videoRef.current?.play().catch(() => {});
      },
    }));

    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;
      setError("");
      setQualities([]); setQuality(-1);
      completedRef.current=false;
      const watch = new WatchClock();
      let waiting = false, slowTimer: ReturnType<typeof setTimeout> | undefined;
      const clearSlow = () => { clearTimeout(slowTimer); setSlow(false); };
      const sample = () => {
        const seconds = watch.sample(performance.now(), video.currentTime, video.playbackRate,
          !video.paused && !video.ended && !video.seeking && !waiting && !document.hidden && video.readyState >= 3);
        if (seconds > 0) watchRef.current?.(seconds);
      };
      const resetWatch = () => watch.reset();
      const paused = () => { watch.reset(); clearSlow(); pauseRef.current?.(); };
      const stalled = () => { waiting = true; watch.reset(); clearTimeout(slowTimer); slowTimer = setTimeout(() => setSlow(true), 3000); };
      const resumed = () => { waiting = false; autoRecovery.current = 0; watch.reset(); clearSlow(); };
      const watchTimer = setInterval(sample, 250);
      video.addEventListener("pause", paused);
      video.addEventListener("seeking", resetWatch);
      video.addEventListener("seeked", resetWatch);
      video.addEventListener("ratechange", resetWatch);
      video.addEventListener("waiting", stalled);
      video.addEventListener("playing", resumed);
      document.addEventListener("visibilitychange", resetWatch);

      const reportProgress = () => {
        setCurrentTime(Math.max(0, video.currentTime));
        progressRef.current?.(Math.max(0, video.currentTime));
      };

      const restoreProgress = () => {
        const saved = retryPosition.current ?? initialTime;
        const maximum = video.duration;
        if (Number.isFinite(maximum) && maximum > 0) {
          durationRef.current?.(maximum);
          setDuration(maximum);
          // Only the initial start skips the opening 30 seconds. Once loaded,
          // users may seek anywhere, including back into the opening segment.
          video.currentTime = saved > 0 && saved < maximum - 1
            ? saved
            : Math.min(trimStart, Math.max(0, maximum - 1));
        }
        retryPosition.current = null;
        watch.reset();
        void video.play().catch(() => {
          // Mobile browsers commonly reject unmuted autoplay. Muted autoplay
          // keeps the requested immediate-start behavior and lets the user
          // restore sound from the custom volume control.
          video.muted = true;
          setMuted(true);
          void video.play().then(() => flashGesture("center", "已静音自动播放，点击音量开启声音", 2600)).catch(() => {
            setPaused(true);
            setControlsVisible(true);
          });
        });

      };

      video.addEventListener("loadedmetadata", restoreProgress);
      video.addEventListener("timeupdate", reportProgress);
      const handlePlay = () => { setPaused(false); scheduleControls(); playRef.current?.(); };
      const handlePlaying=()=>{setPaused(false);playingRef.current?.()},handleWaiting=()=>waitingRef.current?.(),handleEnded=()=>{setPaused(true);setControlsVisible(true);if(!completedRef.current){completedRef.current=true;endedRef.current?.()}},handleError=()=>{setPaused(true);setControlsVisible(true);setError("视频暂时无法播放，请重试");clearSlow();const mediaError=video.error;errorRef.current?.({source:"native",type:`media_${mediaError?.code||0}`,detail:(mediaError?.message||"HTML5 video error").slice(0,200),fatal:true,host:sourceHost(src),position:Math.round(video.currentTime||0),readyState:video.readyState,networkState:video.networkState})};
      const handlePauseState=()=>{setPaused(true);setControlsVisible(true)};
      const handleEnterPictureInPicture=()=>setPictureInPicture(true),handleLeavePictureInPicture=()=>setPictureInPicture(false);
      video.addEventListener("play", handlePlay);
      video.addEventListener("pause", handlePauseState);
      video.addEventListener("enterpictureinpicture", handleEnterPictureInPicture);
      video.addEventListener("leavepictureinpicture", handleLeavePictureInPicture);
      video.addEventListener("playing",handlePlaying);video.addEventListener("waiting",handleWaiting);video.addEventListener("ended",handleEnded);video.addEventListener("error",handleError);

      let hls: Hls | undefined;
      if (Hls.isSupported()) {
        hls = new Hls({ enableWorker: true, maxBufferLength: 30 });
        hlsRef.current = hls;
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
          const seen = new Set<string>();
          const options = data.levels.map((level, index) => {
            const height = Number(level.height) || 0, bitrate = Number(level.bitrate) || 0;
            const label = height > 0 ? `${height}P` : bitrate > 0 ? `${Math.round(bitrate / 1000)}K` : `线路${index + 1}`;
            return { index, label };
          }).filter(item => seen.has(item.label) ? false : (seen.add(item.label), true));
          setQualities(options);
        });
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) {
            const response=data.response as {code?:number;url?:string}|undefined;
            errorRef.current?.({source:"hls",type:String(data.type||"hls_error").slice(0,200),detail:String(data.details||"unknown").slice(0,200),fatal:true,httpStatus:Number(response?.code)||undefined,host:sourceHost(response?.url||data.url||src),position:Math.round(video.currentTime||0),readyState:video.readyState,networkState:video.networkState});
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR && autoRecovery.current < 2) {
              autoRecovery.current += 1;
              retryPosition.current = Math.max(0, video.currentTime || initialTime || trimStart);
              setSlow(true); retryRef.current?.();
              window.setTimeout(() => hls?.startLoad(retryPosition.current || -1), 800);
            } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR && autoRecovery.current < 2) {
              autoRecovery.current += 1;
              setSlow(true); retryRef.current?.();
              window.setTimeout(() => hls?.recoverMediaError(), 500);
            } else if (autoRecovery.current < 2) {
              autoRecovery.current += 1;
              retryPosition.current = Math.max(0, video.currentTime || initialTime || trimStart);
              setSlow(true); retryRef.current?.();
              window.setTimeout(() => setAttempt(value => value + 1), 800);
            } else setError("视频线路暂时无法播放，请重试或稍后再看");
          }
        });
      } else if (video.canPlayType("application/vnd.apple.mpegurl"))
        video.src = src;
      else {setError("当前浏览器不支持 HLS 播放");errorRef.current?.({source:"unsupported",type:"hls_unsupported",detail:"Browser does not support HLS",fatal:true,host:sourceHost(src),position:0,readyState:video.readyState,networkState:video.networkState})}

      return () => {
        clearInterval(watchTimer); clearTimeout(slowTimer);
        video.removeEventListener("pause", paused);
        video.removeEventListener("seeking", resetWatch);
        video.removeEventListener("seeked", resetWatch);
        video.removeEventListener("ratechange", resetWatch);
        video.removeEventListener("waiting", stalled);
        video.removeEventListener("playing", resumed);
        document.removeEventListener("visibilitychange", resetWatch);
        video.removeEventListener("loadedmetadata", restoreProgress);
        video.removeEventListener("timeupdate", reportProgress);
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePauseState);
        video.removeEventListener("enterpictureinpicture", handleEnterPictureInPicture);
        video.removeEventListener("leavepictureinpicture", handleLeavePictureInPicture);
        video.removeEventListener("playing",handlePlaying);video.removeEventListener("waiting",handleWaiting);video.removeEventListener("ended",handleEnded);video.removeEventListener("error",handleError);
        hls?.destroy();
        if (hlsRef.current === hls) hlsRef.current = null;
      };
    }, [src, trimStart, initialTime, attempt]);

    const retry = () => {
      retryPosition.current = Math.max(0, videoRef.current?.currentTime || trimStart);
      videoRef.current?.pause();
      setSlow(false); retryRef.current?.(); setAttempt(value => value + 1);
    };

    return (
      <div
        ref={playerRef}
        className={`${immersive ? "fixed inset-0 z-[2147482000] h-[100dvh] w-screen" : "relative aspect-video w-full"} group overflow-hidden bg-black`}
        onMouseMove={scheduleControls}
      >
        <video
          ref={videoRef}
          poster={poster}
          title={title}
          autoPlay
          playsInline
          preload="metadata"
          onVolumeChange={event => { setVolume(event.currentTarget.volume); setMuted(event.currentTarget.muted); }}
          onRateChange={event => setPlaybackRate(event.currentTarget.playbackRate)}
          className="h-full w-full object-contain"
        />
        <div
          className="absolute inset-0 z-[5] touch-pan-y select-none"
          onPointerDown={beginGesture}
          onPointerMove={moveGesture}
          onPointerUp={endGesture}
          onPointerCancel={cancelGesture}
          onContextMenu={event => event.preventDefault()}
          aria-label="播放器手势区域"
        />
        {gestureHint && <div className={`pointer-events-none absolute top-1/2 z-[8] -translate-y-1/2 rounded-full bg-black/70 px-4 py-3 text-sm font-bold text-white backdrop-blur ${gestureHint.zone === "left" ? "left-[12%]" : gestureHint.zone === "right" ? "right-[12%]" : "left-1/2 -translate-x-1/2"}`}>
          <i className={`${gestureHint.zone === "left" ? "ri-replay-10-line" : gestureHint.zone === "right" ? "ri-forward-10-line" : paused ? "ri-pause-fill" : "ri-play-fill"} mr-1`} />{gestureHint.text}
        </div>}
        {scrubPreview && <div className="pointer-events-none absolute bottom-[78px] z-[9] rounded-full bg-black/80 px-2.5 py-1 text-[11px] font-bold tabular-nums text-white shadow-lg" style={scrubPreviewStyle(scrubPreview.time)}>{formatTime(scrubPreview.time)}</div>}
        {!error && (
          <div className={`absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/90 via-black/55 to-transparent px-3 pb-[max(10px,env(safe-area-inset-bottom))] pt-10 text-white transition-opacity duration-200 ${controlsVisible || paused ? "opacity-100" : "pointer-events-none opacity-0"}`}>
            <input
              aria-label="播放进度"
              type="range"
              min={0}
              max={Math.max(duration, 0.01)}
              step="0.1"
              value={Math.min(currentTime, Math.max(duration, 0.01))}
              onPointerDown={beginProgressScrub}
              onChange={event => updateProgressScrub(Number(event.target.value))}
              onPointerUp={finishProgressScrub}
              onPointerCancel={finishProgressScrub}
              onKeyUp={finishProgressScrub}
              className="h-1.5 w-full cursor-pointer accent-primary-500"
            />
            <div className="mt-2 flex min-w-0 items-center gap-1.5">
              <button type="button" onClick={togglePlayback} aria-label={paused ? "播放" : "暂停"} className="grid h-9 w-9 flex-none place-items-center rounded-full bg-white/15 text-xl">
                <i className={paused ? "ri-play-fill" : "ri-pause-fill"} />
              </button>
              <span className="min-w-0 flex-1 whitespace-nowrap text-[11px] tabular-nums text-white/90">{formatTime(currentTime)} / {formatTime(duration)}</span>
              <button type="button" onClick={toggleMute} aria-label={muted ? "取消静音" : "静音"} className="grid h-8 w-8 flex-none place-items-center rounded-full bg-white/10 text-base"><i className={muted || volume === 0 ? "ri-volume-mute-line" : volume < 0.5 ? "ri-volume-down-line" : "ri-volume-up-line"} /></button>
              <input aria-label="音量" type="range" min={0} max={1} step={0.05} value={muted ? 0 : volume} onChange={event => changeVolume(Number(event.target.value))} className="hidden h-1 w-16 min-w-0 accent-primary-500 min-[480px]:block" />
              <select aria-label="播放速度" value={playbackRate} onChange={event => changePlaybackRate(Number(event.target.value))} className="h-8 w-[46px] flex-none rounded-md border-0 bg-white/10 px-1 text-[11px] text-white outline-none [&>option]:text-black">
                {[0.5, 0.75, 1, 1.25, 1.5, 2].map(value => <option key={value} value={value}>{value}x</option>)}
              </select>
              {qualities.length > 1 && <select aria-label="清晰度" value={quality} onChange={event => changeQuality(Number(event.target.value))} className="h-8 w-[54px] flex-none rounded-md border-0 bg-white/10 px-1 text-[11px] text-white outline-none [&>option]:text-black"><option value={-1}>自动</option>{qualities.map(item => <option key={item.index} value={item.index}>{item.label}</option>)}</select>}
              {"pictureInPictureEnabled" in document && <button type="button" onClick={() => void togglePictureInPicture()} aria-label={pictureInPicture ? "退出画中画" : "画中画"} className={`grid h-8 w-8 flex-none place-items-center rounded-full text-base ${pictureInPicture ? "bg-primary-500" : "bg-white/10"}`}><i className="ri-picture-in-picture-line" /></button>}
              <button type="button" onClick={() => void toggleFullscreen()} aria-label={fullscreen || immersive ? "退出全屏" : "全屏播放"} className="grid h-9 w-9 flex-none place-items-center rounded-full bg-white/15 text-lg">
                <i className={fullscreen || immersive ? "ri-fullscreen-exit-line" : "ri-fullscreen-line"} />
              </button>
            </div>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 grid place-items-center bg-black/80 px-8 text-center text-sm text-white">
            <div role="alert"><p>{error}</p><button onClick={retry} className="mt-4 rounded-full bg-primary-500 px-5 py-3 font-bold">重试播放</button><p className="mt-2 text-xs text-white/70">保留当前进度</p></div>
          </div>
        )}
        {slow && !error && <div role="status" className="absolute inset-x-3 top-3 rounded-lg bg-black/80 p-3 text-center text-xs text-white">网络较慢，正在重新连接视频线路<button onClick={retry} className="ml-3 rounded bg-primary-500 px-3 py-2">立即重试</button></div>}
      </div>
    );
  },
);

VideoPlayer.displayName = "VideoPlayer";
export default VideoPlayer;
